import React, { useState, useRef, useEffect } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { createPortal } from "react-dom";

const UaucOnHoldDetailHelpDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((s) => !s);
  const drawerContentRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (drawerContentRef.current && !drawerContentRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const drawer = (
    <div className="drawer-portal">
      <Drawer
        open={isOpen}
        onClose={toggle}
        direction="right"
        size={500}
        duration={400}
        overlayOpacity={0.5}
        overlayColor="#000"
        customIdSuffix="help-guide-onhold-detail"
        className="help-guide-drawer"
        style={{ top: "73px", height: "calc(100vh - 73px)", transition: "all 0.4s ease-in-out" }}
      >
        <div className="drawer-content" ref={drawerContentRef}>
  <div className="drawer-header">
    <div className="header-title">
      <span className="emoji">📝</span>
      <span className="title">UAUC On-Hold Detail Page – Help Guide</span>
    </div>
    <button className="close-button" onClick={toggle} aria-label="Close help panel">
      &times;
    </button>
  </div>

  <div className="drawer-body">
    <p>
      The UAUC Detail Page provides a comprehensive view of a reported Unsafe Act (UA) or
      Unsafe Condition (UC), along with related information, evidence, assignments, escalations,
      and team collaboration tools.
    </p>

    <h5 className="section-title">Header Information</h5>
    <hr />
    <p>At the top, you will see:</p>
    <ul>
      <li>• Record Number (e.g., 940)</li>
      <li>• UAUC Type (Unsafe Act or Unsafe Condition)</li>
      <li>• Activity Name (e.g., Bar Bending)</li>
      <li>• Reported Month (e.g., July)</li>
    </ul>

    <p>Below this, a timeline view displays:</p>
    <ul>
      <li>• Reported Details</li>
      <li>• Assigned To Details</li>
      <li>• Overdue Details</li>
      <li>• Invited Users</li>
      <li>• Latest Comment</li>
    </ul>

    <h5 className="section-title">Reported Details</h5>
    <hr />
    <p>This section captures key details of the reported UAUC:</p>
    <ul>
      <li>• Evidence (uploaded image of the UAUC)</li>
      <li>• Observation (description of the unsafe act or condition)</li>
      <li>• Hazard Type</li>
      <li>• Risk Level</li>
      <li>• Source (Rules) under which the violation occurred</li>
    </ul>

    <h5 className="section-title">Activity Info</h5>
    <hr />
    <p>Provides contextual details about where the UAUC was reported:</p>
    <ul>
      <li>• Service and its respective Plant / Project / Client</li>
      <li>• Barrier(s) captured during reporting</li>
      <li>• Sub-Hazard linked to the hazard type</li>
      <li>• Department / Contractor / Location associated with the UAUC</li>
    </ul>

    <h5 className="section-title">Control Measures</h5>
    <hr />
    <p>
      Displays the list of missing or violated control measures that were not used during
      the activity.
    </p>

    <h5 className="section-title">Assigned Details & Team Collaboration</h5>
    <hr />
    <ul>
      <li>• <strong>Assigned User:</strong> The person responsible for resolving the UAUC</li>
      <li>• <strong>Invited Team Members:</strong> Users added to collaborate on resolution</li>
    </ul>
    <p>
      To invite: click the <em>Invite User</em> icon (top bar) → select team members in the
      pop-up → notifications will be sent.
    </p>

    <h5 className="section-title">Violator Details</h5>
    <hr />
    <p>Names of violators involved in the unsafe act/condition (captured during reporting).</p>

    <h5 className="section-title">Document Section</h5>
    <hr />
    <p>Allows uploading of supporting documents.</p>
    <ul>
      <li>• Click <em>Add Document</em> → select and upload files.</li>
    </ul>

    <h5 className="section-title">Escalation Details</h5>
    <hr />
    <p>
      Displays escalation levels if a UAUC is not resolved on/before the due date.
      Example: <strong>L1</strong> indicates the first escalation level as per the escalation
      matrix (shown in org-chart format).
    </p>

    <h5 className="section-title">Overdue Section</h5>
    <hr />
    <p>
      Shows the number of days overdue if the UAUC resolution exceeds the due date.
    </p>

    <h5 className="section-title">Discussion Section</h5>
    <hr />
    <p>A chat-based collaboration space for team members.</p>
    <p>Purpose:</p>
    <ul>
      <li>• Discuss resolution approaches</li>
      <li>• Plan risk reduction strategies</li>
      <li>• Share ideas for long-term prevention of incidents/injuries</li>
    </ul>

    <h5 className="section-title">Analysis Panel (Right-Hand Side)</h5>
    <hr />
    <p>Provides detailed analytics related to the activity:</p>
    <ul>
      <li>• IRA Analysis (accident statistics)</li>
      <li>• Near-Miss Analysis</li>
      <li>• UA vs UC Analysis</li>
      <li>• Risk-Based Analysis</li>
      <li>• Good Act & TBT Count</li>
      <li>• Internal & External Training Count</li>
      <li>• Employee & Contractor Training Count</li>
    </ul>
    <p>
      This data helps in building data-driven strategies to reduce future UAUC incidents.
    </p>

    <h5 className="section-title">Editing UAUC</h5>
    <hr />
    <p>
      Click the <em>Edit</em> icon (top right corner) to modify a UAUC.  
      <strong>Note:</strong> Only the user who reported the UAUC can edit their own records.
    </p>
  </div>
</div>


      </Drawer>
    </div>
  );

  return (
    <>
      <div
        className="toggle-help-btn"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
        style={{ fontSize: "16px", cursor: "pointer" }}
      >
        Page Help
        <img src="/images/formkit_help.svg" alt="" />
      </div>
      {createPortal(drawer, document.body)}
    </>
  );
};

export default UaucOnHoldDetailHelpDrawer;



